import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { HorseRead, HorseFilters, UserRead, UserHorseCollectionRead, Country, ProfileWithHorseCount, ProfileRead } from '../types/api';
import { apiService } from '../services/api';
import { urlParams, ViewMode } from '../utils/urlParams';
import { FilterControls } from './FilterControls';
import { FilterChips } from './FilterChips';
import { ResultsGrid } from './ResultsGrid';
import { Login } from './Login';
import { MediaManager } from './MediaManager';
import { AppHeader } from './AppHeader';
import { Image, X, ChevronLeft, Pencil, RotateCcw } from 'lucide-react';
import { CreatePrivateHorseModal } from './PrivateHorseModals';
import { ProfileListView } from './ProfileListView';
import { ProfileEditModal } from './ProfileEditModal';
import { ProfileSelectionPopup } from './ProfileSelectionPopup';
import { OnboardingCards } from './OnboardingCards';

const PAGE_SIZE = 24;

const LAST_SEARCH_KEY = 'topbred_last_search';

interface SavedSearch {
  url: string;
  description: string;
}

function describeFilters(filters: HorseFilters): string {
  const parts: string[] = [];

  if (filters.name) parts.push(`Name: ${filters.name}`);
  if (filters.fei_id) parts.push(`FEI: ${filters.fei_id}`);

  if (filters.birth_year_min !== undefined || filters.birth_year_max !== undefined) {
    if (filters.birth_year_min !== undefined && filters.birth_year_max !== undefined) {
      parts.push(filters.birth_year_min === filters.birth_year_max
        ? `Born ${filters.birth_year_min}`
        : `Born ${filters.birth_year_min}–${filters.birth_year_max}`);
    } else if (filters.birth_year_min !== undefined) {
      parts.push(`Born ≥ ${filters.birth_year_min}`);
    } else {
      parts.push(`Born ≤ ${filters.birth_year_max}`);
    }
  }

  if (filters.show_level_min !== undefined || filters.show_level_max !== undefined) {
    if (filters.show_level_min !== undefined && filters.show_level_max !== undefined) {
      parts.push(`Level ${filters.show_level_min}–${filters.show_level_max} cm`);
    } else if (filters.show_level_min !== undefined) {
      parts.push(`Level ≥ ${filters.show_level_min} cm`);
    } else {
      parts.push(`Level ≤ ${filters.show_level_max} cm`);
    }
  }

  if (filters.sex?.length) parts.push(filters.sex.join(', '));

  if (filters.countries?.length) parts.push(filters.countries.join(', '));

  if (filters.height_cm_min !== undefined || filters.height_cm_max !== undefined) {
    if (filters.height_cm_min !== undefined && filters.height_cm_max !== undefined) {
      parts.push(`Height ${filters.height_cm_min}–${filters.height_cm_max} cm`);
    } else if (filters.height_cm_min !== undefined) {
      parts.push(`Height ≥ ${filters.height_cm_min} cm`);
    } else {
      parts.push(`Height ≤ ${filters.height_cm_max} cm`);
    }
  }

  return parts.length > 0 ? parts.join(' · ') : 'All horses';
}

function loadSavedSearch(): SavedSearch | null {
  try {
    const raw = localStorage.getItem(LAST_SEARCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.url === 'string' && typeof parsed.description === 'string') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function persistSearch(url: string, filters: HorseFilters): SavedSearch {
  const saved: SavedSearch = { url, description: describeFilters(filters) };
  localStorage.setItem(LAST_SEARCH_KEY, JSON.stringify(saved));
  return saved;
}

export const SearchResults: React.FC = () => {
  const [horses, setHorses] = useState<HorseRead[]>([]);
  const [totalHorses, setTotalHorses] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [error, setError] = useState<string | undefined>();
  const [filters, setFilters] = useState<HorseFilters>(() => urlParams.getFiltersFromUrl());
  const [user, setUser] = useState<UserRead | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [collectionItems, setCollectionItems] = useState<UserHorseCollectionRead[]>([]);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [collectionError, setCollectionError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => urlParams.getViewModeFromUrl());
  const [showCreatePrivateHorseModal, setShowCreatePrivateHorseModal] = useState(false);

  const [showStartDialog, setShowStartDialog] = useState(false);
  const [startDialogFilters, setStartDialogFilters] = useState<HorseFilters>(filters);
  const [isCollectionSearch, setIsCollectionSearch] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [lastSearch, setLastSearch] = useState<SavedSearch | null>(loadSavedSearch);

  // Profile state
  const [profiles, setProfiles] = useState<ProfileWithHorseCount[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  // undefined = showing profile list, null = All Horses, string = specific profile id
  const [selectedProfileId, setSelectedProfileId] = useState<string | null | undefined>(() => {
    const urlViewMode = urlParams.getViewModeFromUrl();
    return urlViewMode === 'collection' ? urlParams.getProfileIdFromUrl() : undefined;
  });
  // undefined = no status filter, string = specific status (e.g. 'watch', 'skip')
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(() => {
    const urlViewMode = urlParams.getViewModeFromUrl();
    return urlViewMode === 'collection' ? urlParams.getStatusFromUrl() : undefined;
  });
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<ProfileWithHorseCount | null>(null);
  const [profileAssignmentPending, setProfileAssignmentPending] = useState<{
    horseId: string;
    collectionItem: UserHorseCollectionRead;
  } | null>(null);

  const collectionHorseIds = useMemo(
    () => new Set(collectionItems.map(item => item.horse_id)),
    [collectionItems]
  );
  const collectionData = useMemo(
    () => new Map(collectionItems.map(item => [item.horse_id, item])),
    [collectionItems]
  );
  const collectionHorseNames = useMemo(
    () => collectionItems.map(i => i.horse?.unified_horse_name).filter((n): n is string => Boolean(n)),
    [collectionItems]
  );

  // Monotonic counter to discard stale loadHorses responses
  const loadHorsesIdRef = useRef(0);

  // Handle expanding to horse detail view
  const handleExpandClick = (horseId: string) => {
    urlParams.navigateToHorseDetail(horseId);
  };

  const handleSelectProfile = useCallback((profileId: string | null | undefined) => {
    setSelectedProfileId(profileId);
    setSelectedStatus(undefined);
    urlParams.updateUrlWithFiltersAndView(filters, 'collection', profileId);
  }, [filters]);

  const handleSelectStatus = useCallback((status: string | undefined) => {
    setSelectedStatus(status);
    setSelectedProfileId(undefined);
    urlParams.updateUrlWithFiltersAndView(filters, 'collection', undefined, status);
  }, [filters]);

  const horsePassesFilters = useCallback(
    (horse: HorseRead, currentFilters: HorseFilters) => {
      if (currentFilters.name) {
        const needle = currentFilters.name.toLowerCase();
        const matchesName = horse.unified_horse_name?.toLowerCase().includes(needle);
        if (!matchesName) return false;
      }

      if (currentFilters.fei_id) {
        const needle = currentFilters.fei_id.trim().toLowerCase();
        if (!horse.unified_fei_id || !horse.unified_fei_id.toLowerCase().includes(needle)) {
          return false;
        }
      }

      if (currentFilters.birth_year_min !== undefined) {
        if (!horse.unified_birth_year || horse.unified_birth_year < currentFilters.birth_year_min) return false;
      }
      if (currentFilters.birth_year_max !== undefined) {
        if (!horse.unified_birth_year || horse.unified_birth_year > currentFilters.birth_year_max) return false;
      }

      if (currentFilters.sex && currentFilters.sex.length) {
        if (!horse.unified_sex || !currentFilters.sex.includes(horse.unified_sex)) return false;
      }

      if (currentFilters.show_level_min !== undefined) {
        if (horse.horse_level === null || horse.horse_level < currentFilters.show_level_min) return false;
      }
      if (currentFilters.show_level_max !== undefined) {
        if (horse.horse_level === null || horse.horse_level > currentFilters.show_level_max) return false;
      }

      return true;
    },
    []
  );

  const loadHorses = useCallback(async (
    currentFilters: HorseFilters,
    currentViewMode: ViewMode,
    skip: number = 0,
    append: boolean = false
  ) => {
    if (currentViewMode === 'media' || currentViewMode === 'collection') {
      return;
    }

    const thisLoadId = ++loadHorsesIdRef.current;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(undefined);

    try {
      let horsesData: HorseRead[];
      if (currentFilters.fei_id?.trim()) {
        try {
          const horse = await apiService.getHorseByFeiId(currentFilters.fei_id.trim());
          horsesData = [horse];
          if (loadHorsesIdRef.current !== thisLoadId) return;
          setTotalHorses(1);
        } catch (err) {
          if (loadHorsesIdRef.current !== thisLoadId) return;
          const msg = err instanceof Error ? err.message : '';
          if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
            horsesData = [];
            setTotalHorses(0);
          } else {
            throw err;
          }
        }
      } else {
        const response = await apiService.searchHorses(currentFilters, skip, PAGE_SIZE);
        if (loadHorsesIdRef.current !== thisLoadId) return;
        horsesData = response.horses;
        setTotalHorses(response.total);
      }
      if (append) {
        setHorses(prev => [...prev, ...horsesData]);
      } else {
        setHorses(horsesData);
      }
    } catch (err) {
      if (loadHorsesIdRef.current !== thisLoadId) return;
      setError(err instanceof Error ? err.message : 'Failed to load horses');
    } finally {
      if (loadHorsesIdRef.current === thisLoadId) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const loadCollection = useCallback(async () => {
    setCollectionLoading(true);
    setCollectionError(null);
    try {
      const collection = await apiService.getCollection();
      setCollectionItems(prev => collection.map(newItem => {
        // Preserve profile assignments if the API response omits them
        if (newItem.profiles !== undefined) return newItem;
        const existing = prev.find(p => p.horse_id === newItem.horse_id);
        return existing ? { ...newItem, profiles: existing.profiles } : newItem;
      }));
      return true;
    } catch (error) {
      console.error('Failed to load collection:', error);
      setCollectionError(error instanceof Error ? error.message : 'Failed to load collection');
      return false;
    } finally {
      setCollectionLoading(false);
    }
  }, []);

  const loadProfiles = useCallback(async () => {
    setProfilesLoading(true);
    try {
      const data = await apiService.getProfiles();
      setProfiles(data);
    } catch (error) {
      // Non-fatal — profiles are optional
      console.warn('Failed to load profiles:', error);
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  // Clear horses if viewing collection while unauthenticated
  useEffect(() => {
    if (viewMode === 'collection' && !apiService.isAuthenticated()) {
      setHorses([]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check authentication status and load collection + profiles
  useEffect(() => {
    const initializeAuthenticatedData = async () => {
      if (apiService.isAuthenticated()) {
        loadUser();
        await loadCollection();
        loadProfiles();
        apiService.getCountries().then(setCountries).catch(() => {});
      }
    };

    initializeAuthenticatedData();
  }, [loadCollection, loadProfiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load horses when filters or view mode change (non-collection views)
  useEffect(() => {
    if (viewMode !== 'collection') {
      setCurrentSkip(0);
      loadHorses(filters, viewMode, 0, false);
    }
  }, [filters, viewMode, loadHorses]);

  // Handle browser navigation (back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      const urlFilters = urlParams.getFiltersFromUrl();
      const urlViewMode = urlParams.getViewModeFromUrl();

      setFilters(urlFilters);
      setViewMode(urlViewMode);
      if (urlViewMode === 'collection') {
        setSelectedProfileId(urlParams.getProfileIdFromUrl());
        setSelectedStatus(urlParams.getStatusFromUrl());
      } else {
        setCurrentSkip(0);
        loadHorses(urlFilters, urlViewMode, 0, false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('urlchange', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('urlchange', handlePopState);
    };
  }, [loadHorses]);

  useEffect(() => {
    const handleCollectionRefresh = async () => {
      await loadCollection();
    };
    window.addEventListener('collection:refresh', handleCollectionRefresh);
    return () => window.removeEventListener('collection:refresh', handleCollectionRefresh);
  }, [loadCollection]);

  useEffect(() => {
    if (viewMode !== 'collection') {
      setShowCreatePrivateHorseModal(false);
      if (selectedProfileId !== undefined) {
        setSelectedProfileId(undefined);
      }
      if (selectedStatus !== undefined) {
        setSelectedStatus(undefined);
      }
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for profile assignment requests dispatched by HorseCard after Watch
  useEffect(() => {
    const handleProfilesAssign = (e: Event) => {
      const { horseId, collectionItem } = (e as CustomEvent).detail;
      setProfileAssignmentPending({ horseId, collectionItem });
    };
    window.addEventListener('profiles:assign', handleProfilesAssign);
    return () => window.removeEventListener('profiles:assign', handleProfilesAssign);
  }, []);

  const loadUser = async () => {
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      apiService.logout();
      setUser(null);
    }
  };

  const handleApplyFilters = useCallback((newFilters: HorseFilters) => {
    setFilters(newFilters);
    urlParams.updateUrlWithFiltersAndView(newFilters, viewMode, viewMode === 'collection' ? selectedProfileId : undefined);
  }, [viewMode, selectedProfileId]);

  const handleRemoveFilter = useCallback((filterKey: keyof HorseFilters) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    if (filterKey === 'last_result_within') delete newFilters.last_result_unit;
    if (filterKey === 'last_result_unit') delete newFilters.last_result_within;
    setFilters(newFilters);
    urlParams.updateUrlWithFiltersAndView(newFilters, viewMode, viewMode === 'collection' ? selectedProfileId : undefined);
  }, [filters, viewMode, selectedProfileId]);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    urlParams.updateUrlWithFiltersAndView({}, viewMode, viewMode === 'collection' ? selectedProfileId : undefined);
  }, [viewMode, selectedProfileId]);

  const handleLoadMore = useCallback(() => {
    const nextSkip = currentSkip + PAGE_SIZE;
    setCurrentSkip(nextSkip);
    loadHorses(filters, viewMode, nextSkip, true);
  }, [currentSkip, filters, viewMode, loadHorses]);

  const hasMore = viewMode !== 'collection' && totalHorses !== null && horses.length < totalHorses;

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'collection') {
      urlParams.updateUrlWithFiltersAndView(filters, mode, selectedProfileId);
      if (apiService.isAuthenticated() && collectionItems.length === 0) {
        loadCollection();
      }
    } else {
      urlParams.updateUrlWithFiltersAndView(filters, mode);
    }
  }, [filters, collectionItems.length, loadCollection, selectedProfileId]);

  const handleStartDialogApply = useCallback(
    (newFilters: HorseFilters) => {
      setFilters(newFilters);
      setViewMode('all');
      urlParams.updateUrlWithFiltersAndView(newFilters, 'all');
      setLastSearch(persistSearch(window.location.href, newFilters));
      setShowStartDialog(false);
    },
    []
  );

  const handleResumeSearch = useCallback(() => {
    if (!lastSearch) return;
    const newFilters = urlParams.parseFiltersFromSearchString(lastSearch.url);
    setFilters(newFilters);
    setViewMode('all');
    urlParams.updateUrlWithFiltersAndView(newFilters, 'all');
  }, [lastSearch]);

  const handleOnboardingSearch = useCallback((searchUri: string) => {
    const newFilters = urlParams.parseFiltersFromSearchString(searchUri);
    setFilters(newFilters);
    setViewMode('all');
    urlParams.updateUrlWithFiltersAndView(newFilters, 'all');
    setLastSearch(persistSearch(window.location.href, newFilters));
  }, []);

  const handleSearchMyHorsesApply = useCallback(
    (newFilters: HorseFilters) => {
      setFilters(newFilters);
      setSelectedProfileId(null);
      setSelectedStatus(undefined);
      urlParams.updateUrlWithFiltersAndView(newFilters, 'collection', null);
      setShowStartDialog(false);
    },
    []
  );

  const matchesCollectionFilters = useCallback((item: UserHorseCollectionRead) => {
    const horse = item.horse;
    if (!horse) {
      return false;
    }

    if (filters.in_collection === false) {
      return false;
    }

    if (filters.is_private !== undefined && item.is_private !== filters.is_private) {
      return false;
    }

    if (filters.private_name && !(item.private_name || '').toLowerCase().includes(filters.private_name.toLowerCase())) {
      return false;
    }

    return horsePassesFilters(horse, filters);
  }, [filters, horsePassesFilters]);

  const searchHorses = useMemo(() => {
    return horses.filter((horse) => horsePassesFilters(horse, filters));
  }, [filters, horsePassesFilters, horses]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of collectionItems) {
      counts[item.status] = (counts[item.status] ?? 0) + 1;
    }
    return counts;
  }, [collectionItems]);

  const collectionHorses = useMemo(() => {
    if (viewMode !== 'collection') {
      return [] as HorseRead[];
    }

    let items = collectionItems;

    // Filter by status when a status folder is selected
    if (selectedStatus) {
      items = items.filter(item => item.status === selectedStatus);
    } else if (selectedProfileId !== undefined) {
      // Profile or "All Horses" view: show all non-skipped statuses
      items = items.filter(item => item.status !== 'skip');
    }

    // Filter by profile if a specific profile is selected
    if (typeof selectedProfileId === 'string') {
      items = items.filter(item =>
        (item.profiles ?? []).some(p => p.id === selectedProfileId)
      );
    }

    return items
      .filter(item => matchesCollectionFilters(item))
      .map(item => item.horse)
      .filter((horse): horse is HorseRead => Boolean(horse));
  }, [collectionItems, matchesCollectionFilters, viewMode, selectedProfileId, selectedStatus]);

  const displayedHorses = viewMode === 'collection' ? collectionHorses : searchHorses;
  const activeLoading = viewMode === 'collection' ? collectionLoading : loading;
  const activeError = viewMode === 'collection' ? collectionError || undefined : error;
  const hasFilters = useMemo(() => Object.keys(filters).length > 0, [filters]);
  const headerTitle = useMemo(() => {
    if (viewMode === 'collection') {
      return `My top prospect horses (${displayedHorses.length})`;
    }
    if (!hasFilters) {
      return 'Top horse suggestions';
    }
    if (totalHorses === null) {
      return 'Searching...';
    }
    const shown = displayedHorses.length;
    if (totalHorses > shown) {
      return `Top ${shown.toLocaleString()} of ${totalHorses.toLocaleString()} horses for your profile`;
    }
    return `Top ${totalHorses.toLocaleString()} horses for your profile`;
  }, [viewMode, displayedHorses.length, totalHorses, hasFilters]);

  const handleCollectionChange = useCallback((horseId: string, added: boolean, collectionItem?: UserHorseCollectionRead) => {
    setCollectionItems(prev => {
      if (added && collectionItem) {
        const filtered = prev.filter(item => item.horse_id !== horseId);
        return [collectionItem, ...filtered];
      }
      return prev.filter(item => item.horse_id !== horseId);
    });
  }, []);

  const handlePrivateHorseCreated = useCallback((collectionItem: UserHorseCollectionRead) => {
    setCollectionItems(prev => [collectionItem, ...prev.filter(item => item.horse_id !== collectionItem.horse_id)]);
    setShowCreatePrivateHorseModal(false);
    window.dispatchEvent(new CustomEvent('collection:refresh'));
    urlParams.navigateToHorseDetail(collectionItem.horse_id);
  }, []);

  const handleLoginSuccess = async () => {
    setShowLogin(false);
    loadUser();
    await loadCollection();
  };

  const handleLogout = () => {
    apiService.logout();
    setUser(null);
    setCollectionItems([]);
  };

  const handleLogoClick = () => {
    setShowStartDialog(false);
    urlParams.resetToHome();
  };

  // Scroll indicator for the scouting modal filter area
  const filterScrollRef = useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const checkFilterScroll = useCallback(() => {
    const el = filterScrollRef.current;
    if (!el) return;
    const canScroll = el.scrollHeight > el.clientHeight + 4;
    const notAtBottom = el.scrollTop + el.clientHeight < el.scrollHeight - 8;
    setShowScrollHint(canScroll && notAtBottom);
  }, []);

  useEffect(() => {
    if (!showStartDialog) return;
    const el = filterScrollRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(checkFilterScroll);
    el.addEventListener('scroll', checkFilterScroll, { passive: true });
    const ro = new ResizeObserver(checkFilterScroll);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', checkFilterScroll);
      ro.disconnect();
    };
  }, [showStartDialog, checkFilterScroll]);

  // Lock body scroll when the Start Scouting dialog is open
  useEffect(() => {
    if (!showStartDialog) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showStartDialog]);

  if (showLogin) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="min-h-screen bg-white pb-16 lg:pb-0">
      {/* Header */}
      <AppHeader
        user={user}
        onLogin={() => setShowLogin(true)}
        onLogout={handleLogout}
        onLogoClick={handleLogoClick}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions (home) */}
        {viewMode === 'all' && (
          <div className="mb-8 space-y-4">
            <div className="flex flex-col items-center justify-center gap-3">
              <button
                onClick={() => {
                  if (!user) {
                    setShowLogin(true);
                    return;
                  }
                  setStartDialogFilters(filters);
                  setIsCollectionSearch(false);
                  setShowStartDialog(true);
                }}
                className="inline-flex items-center justify-center gap-3 w-80 px-8 py-5 text-base font-semibold text-[#132B64] bg-white border-2 border-[#132B64] rounded-full hover:bg-gray-50 transition-colors"
              >
                <img src="/icons/search_icon.svg" alt="" className="h-6 w-6" />
                Start Scouting
              </button>
              {!hasFilters && lastSearch && (
                <button
                  onClick={handleResumeSearch}
                  className="flex flex-col items-center justify-center w-80 px-8 py-3 bg-gray-50 border border-gray-200 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#132B64]">
                    <RotateCcw size={16} />
                    Resume Scouting
                  </span>
                  <span className="text-xs text-gray-500 mt-0.5 truncate max-w-full">
                    {lastSearch.description}
                  </span>
                </button>
              )}
            </div>
            {!hasFilters && <OnboardingCards onSearch={handleOnboardingSearch} />}
          </div>
        )}

        {viewMode === 'media' ? (
          /* Media Management View */
          user ? (
            <MediaManager />
          ) : (
            <div className="text-center py-12">
              <Image size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
              <p className="text-gray-500 mb-4">Please sign in to access your media library.</p>
              <button
                onClick={() => setShowLogin(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Sign In
              </button>
            </div>
          )
        ) : (
          <>
                {viewMode === 'collection' ? (
              selectedProfileId === undefined && selectedStatus === undefined ? (
                /* Profile list landing */
                <ProfileListView
                  profiles={profiles}
                  loading={profilesLoading}
                  totalHorseCount={collectionItems.filter(i => i.status !== 'skip').length}
                  statusCounts={statusCounts}
                  onSelectProfile={handleSelectProfile}
                  onSelectStatus={handleSelectStatus}
                  onEditProfile={(p) => { setEditingProfile(p); setShowProfileEditModal(true); }}
                  onCreateProfile={() => { setEditingProfile(null); setShowProfileEditModal(true); }}
                  onSearchMyHorses={() => {
                    if (!user) { setShowLogin(true); return; }
                    setStartDialogFilters({});
                    setIsCollectionSearch(true);
                    setShowStartDialog(true);
                  }}
                />
              ) : (
                /* Horse list for selected profile, status, or All Horses */
                <>
                  {/* Detail header */}
                  <div className="mb-4 flex justify-center">
                    <div className="w-full max-w-[320px] lg:max-w-[500px] flex items-center gap-2">
                      <button
                        onClick={() => selectedStatus ? handleSelectStatus(undefined) : handleSelectProfile(undefined)}
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 shrink-0"
                        aria-label="Back to profiles"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <h2 className="text-lg font-semibold text-gray-900 flex-1 truncate">
                        {selectedStatus
                          ? ({ watch: 'Watching', own: 'Own', sold: 'Sold', skip: 'Skipped' }[selectedStatus] ?? selectedStatus)
                          : selectedProfileId === null
                            ? 'All Horses'
                            : (profiles.find(p => p.id === selectedProfileId)?.name ?? 'Profile')}
                      </h2>
                      {!selectedStatus && selectedProfileId !== null && (
                        <button
                          onClick={() => {
                            const p = profiles.find(pr => pr.id === selectedProfileId);
                            if (p) { setEditingProfile(p); setShowProfileEditModal(true); }
                          }}
                          className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 shrink-0"
                          aria-label="Edit profile"
                        >
                          <Pencil size={15} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Active Filter Chips */}
                  <FilterChips
                    filters={filters}
                    onRemoveFilter={handleRemoveFilter}
                    onClearAll={handleClearAllFilters}
                    countries={countries}
                  />

                  {/* Results Grid */}
                  <ResultsGrid
                    horses={displayedHorses}
                    loading={activeLoading}
                    error={activeError}
                    collectionHorseIds={collectionHorseIds}
                    collectionData={collectionData}
                    onCollectionChange={handleCollectionChange}
                    onExpandClick={handleExpandClick}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    loadingMore={loadingMore}
                    countries={countries}
                  />
                </>
              )
            ) : (
              <>
            {/* Active Filter Chips */}
            <FilterChips
              filters={filters}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
              countries={countries}
            />

            {/* Results Header - hidden when searching by name */}
            {!filters.name && (
              <div className="mb-6 flex justify-center">
                <div className="w-full max-w-[320px] lg:max-w-[500px] flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {activeLoading && !hasFilters && viewMode === 'all' ? 'Top horse suggestions' : headerTitle}
                  </h2>
                  {!user && (
                    <div className="mt-2 sm:mt-0 text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2">
                      <span className="font-medium">Sign in</span> to manage your horse collection
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Results Grid */}
            <ResultsGrid
              horses={displayedHorses}
              loading={activeLoading}
              error={activeError}
              collectionHorseIds={collectionHorseIds}
              collectionData={collectionData}
              onCollectionChange={handleCollectionChange}
              onExpandClick={handleExpandClick}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              loadingMore={loadingMore}
              countries={countries}
            />
              </>
            )}
          </>
        )}
      </main>
      {showStartDialog && (
        <div className="fixed top-16 lg:top-0 left-0 lg:left-60 right-0 bottom-16 lg:bottom-0 bg-black/35 lg:bg-transparent flex items-center justify-center z-50 px-4 py-4 sm:py-6">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[calc(100vh-10rem)] flex flex-col relative overflow-hidden">
            {/* Header */}
            <div className="p-6 sm:p-7 pb-0 flex items-start justify-between gap-4 shrink-0">
              <h3 className="text-2xl font-semibold text-[#132B64]">
                {isCollectionSearch ? 'Search My Horses' : 'Search'}
              </h3>
              <button
                onClick={() => setShowStartDialog(false)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-gray-100 text-gray-700 shrink-0"
                aria-label="Close"
              >
                <X size={24} strokeWidth={2.5} />
              </button>
            </div>

            {/* Scrollable content */}
            <div ref={filterScrollRef} className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-7 pt-4 relative">
              <FilterControls
                onApplyFilters={isCollectionSearch ? handleSearchMyHorsesApply : handleStartDialogApply}
                currentFilters={startDialogFilters}
                onFiltersChange={setStartDialogFilters}
                forceExpanded
                hideActions
                hideHeader
                countries={countries}
                collectionHorseNames={isCollectionSearch ? collectionHorseNames : undefined}
              />
              {showScrollHint && (
                <div className="sticky bottom-0 left-0 right-0 pointer-events-none" aria-hidden="true">
                  <div className="h-10 bg-gradient-to-t from-white to-transparent" />
                  <div className="bg-white flex justify-center pb-1">
                    <span className="text-xs text-gray-400">Scroll for more filters</span>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky footer */}
            <div className="p-6 sm:p-7 pt-4 border-t border-gray-100 shrink-0 flex justify-center">
              <button
                onClick={() => isCollectionSearch
                  ? handleSearchMyHorsesApply(startDialogFilters)
                  : handleStartDialogApply(startDialogFilters)
                }
                className="px-8 py-3 text-base font-semibold text-white bg-[#132B64] rounded-full hover:bg-[#0f2350] transition-colors"
              >
                Find
              </button>
            </div>
          </div>
        </div>
      )}
      <CreatePrivateHorseModal
        isOpen={showCreatePrivateHorseModal}
        onClose={() => setShowCreatePrivateHorseModal(false)}
        onCreated={handlePrivateHorseCreated}
      />

      {/* Profile edit/create modal */}
      <ProfileEditModal
        isOpen={showProfileEditModal}
        profile={editingProfile}
        onClose={() => setShowProfileEditModal(false)}
        onSaved={(saved) => {
          setProfiles(prev => {
            const idx = prev.findIndex(p => p.id === saved.id);
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = { ...prev[idx], ...saved };
              return updated;
            }
            return [...prev, { ...saved, horse_count: 0 }];
          });
        }}
        onDeleted={(deletedId) => {
          setProfiles(prev => prev.filter(p => p.id !== deletedId));
          if (selectedProfileId === deletedId) handleSelectProfile(undefined);
        }}
      />

      {/* Profile selection popup (after Watch) */}
      <ProfileSelectionPopup
        isOpen={profileAssignmentPending !== null}
        horseId={profileAssignmentPending?.horseId ?? ''}
        profiles={profiles}
        onConfirm={async (selectedIds) => {
          if (selectedIds.length > 0) {
            // Optimistically increment horse_count for assigned profiles
            setProfiles(prev => prev.map(p =>
              selectedIds.includes(p.id) ? { ...p, horse_count: p.horse_count + 1 } : p
            ));
          }
          setProfileAssignmentPending(null);
          await loadCollection();
        }}
        onClose={() => setProfileAssignmentPending(null)}
        onProfileCreated={(newProfile) => {
          setProfiles(prev => [...prev, { ...newProfile, horse_count: 0 }]);
        }}
      />
    </div>
  );
}; 